import React, { useMemo, useCallback } from "react";
import { Paper, Group, Text, Stack, Box, CloseButton } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { useUserProfileContext } from "@/contexts";
import { Expense } from "@/utils/api";
import { AppIcon } from "@/components/generic";
import { formatCurrency } from "@/utils";

// ============================================
// Types
// ============================================

export interface RevenuesSelectionSummaryProps {
  /** Array of selected revenue records */
  selectedRevenues: Expense[];

  /** Callback to clear selection */
  onClearSelection?: () => void;

  /** Whether the summary is visible */
  visible?: boolean;

  /** Position of the summary bar */
  position?: "top" | "bottom";
}

export interface SelectionRevenueSummaryData {
  /** Number of selected items */
  count: number;
  /** Total amount sum */
  totalAmount: number;
  /** Currency of the first selected item */
  currency: string | null;
  /** Whether all selected items have the same currency */
  isSameCurrency: boolean;
}

// ============================================
// Helper Functions
// ============================================

/**
 * Calculate selection summary data from selected revenues
 */
export const calculateSelectionRevenueSummary = (
  selectedRevenues: Expense[]
): SelectionRevenueSummaryData => {
  if (selectedRevenues.length === 0) {
    return {
      count: 0,
      totalAmount: 0,
      currency: null,
      isSameCurrency: false,
    };
  }

  // Calculate totals
  const totalAmount = selectedRevenues.reduce(
    (sum, r) => sum + (r.totalPrice || 0),
    0
  );

  // Get currency from first selected item
  const currency = selectedRevenues[0]?.paidInCurrency || null;

  // Check if all selected have the same currency
  const currencies = new Set(
    selectedRevenues.map((r) => r.paidInCurrency?.toUpperCase()).filter(Boolean)
  );
  const isSameCurrency = currencies.size <= 1;

  return {
    count: selectedRevenues.length,
    totalAmount: Math.round(totalAmount * 100) / 100,
    currency,
    isSameCurrency,
  };
};

// ============================================
// Component
// ============================================

const RevenuesSelectionSummaryComponent: React.FC<
  RevenuesSelectionSummaryProps
> = ({
  selectedRevenues,
  onClearSelection,
  visible = true,
  position = "bottom",
}) => {
  const { t } = useTranslation(["revenues", "common"]);
  const { currency: homeCurrency } = useUserProfileContext();

  // Calculate summary data
  const summary = useMemo(
    () => calculateSelectionRevenueSummary(selectedRevenues),
    [selectedRevenues]
  );

  // Handle clear click
  const handleClear = useCallback(() => {
    onClearSelection?.();
  }, [onClearSelection]);

  // Format amount display
  const formattedAmount = useMemo(() => {
    if (!summary.isSameCurrency) {
      return t("revenues:selection.mixedCurrencies");
    }
    return formatCurrency(summary.totalAmount, summary.currency || homeCurrency);
  }, [summary, homeCurrency, t]);

  // Don't render if no selection or not visible
  if (!visible || summary.count === 0) {
    return null;
  }

  // Position styles - improved for mobile visibility
  const positionStyles: React.CSSProperties = {
    position: "sticky",
    zIndex: 100,
    ...(position === "bottom"
      ? { bottom: 0, marginTop: "auto" }
      : { top: 0, marginBottom: "auto" }),
  };

  return (
    <>
      {/* Mobile layout - enhanced visibility */}
      <Box
        hiddenFrom="sm"
        mx={-12}
        mb={-12}
        style={positionStyles}
      >
        <Paper
          shadow="lg"
          p="md"
          radius={0}
          style={{
            backgroundColor: "var(--mantine-color-blue-0)",
            borderTop: "2px solid var(--mantine-color-blue-4)",
          }}
        >
          <Stack gap="xs">
            {/* Header row with count and close button */}
            <Group justify="space-between" wrap="nowrap">
              <Group gap="xs" wrap="nowrap">
                <Box
                  style={{
                    backgroundColor: "var(--mantine-color-blue-6)",
                    borderRadius: "50%",
                    width: 28,
                    height: 28,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <AppIcon iconName="Check" size={16} color="white" />
                </Box>
                <Text size="md" fw={600} c="blue.8">
                  {t("revenues:selection.selected", { count: summary.count })}
                </Text>
              </Group>
              <CloseButton
                size="md"
                onClick={handleClear}
                aria-label={t("revenues:selection.clear")}
                variant="subtle"
                c="blue.7"
              />
            </Group>

            {/* Total amount - prominent display */}
            <Group gap="xs" wrap="nowrap" align="center">
              <AppIcon iconName="CurrencyDollar" size={20} color="blue.6" />
              <Text size="lg" fw={700} c="blue.8">
                {formattedAmount}
              </Text>
            </Group>
          </Stack>
        </Paper>
      </Box>

      {/* Desktop layout - horizontal */}
      <Box visibleFrom="sm" style={positionStyles}>
        <Paper
          shadow="md"
          p="sm"
          withBorder
          bg="var(--mantine-color-body)"
          style={{
            borderColor: "var(--mantine-color-blue-3)",
          }}
        >
          <Group justify="space-between" wrap="nowrap">
            <Group gap="lg" wrap="nowrap">
              {/* Selection count */}
              <Group gap="xs" wrap="nowrap">
                <Box
                  style={{
                    backgroundColor: "var(--mantine-color-blue-6)",
                    borderRadius: "50%",
                    width: 24,
                    height: 24,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <AppIcon iconName="Check" size={14} color="white" />
                </Box>
                <Text size="sm" fw={600}>
                  {t("revenues:selection.selected", { count: summary.count })}
                </Text>
              </Group>

              <Text c="dimmed">•</Text>

              {/* Total amount */}
              <Group gap={4} wrap="nowrap">
                <Text size="sm" c="dimmed">
                  {t("revenues:selection.total")}:
                </Text>
                <Text size="sm" fw={700} c="blue.7">
                  {formattedAmount}
                </Text>
              </Group>
            </Group>

            <CloseButton
              size="sm"
              onClick={handleClear}
              aria-label={t("revenues:selection.clear")}
            />
          </Group>
        </Paper>
      </Box>
    </>
  );
};

export const RevenuesSelectionSummary = React.memo(
  RevenuesSelectionSummaryComponent
);